import { intervalToDuration } from 'date-fns';
import { useMemo, useState } from 'react';
import { CountdownCircleTimer } from 'react-countdown-circle-timer';
import { toast } from 'sonner';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Spinner, useApiMutation, useApiQuery } from '@/modules/common';

import { InstanceForm } from '../components';
import { ActiveInstance, Config, Configs, StartInstance } from '../types';

const INSTANCE_REFRESH_INTERVAL = 1000 * 30;

export const InstanceManagerPage = () => {
  const [isShuttingDown, setIsShuttingDown] = useState(false);

  const { data: configs } = useApiQuery<Configs[]>(
    '/instance-management/configs',
    {
      initialData: [],
      queryKey: ['configs'],
    }
  );

  const { data, refetch, isFetching } = useApiQuery<ActiveInstance>(
    '/instance-management/active',
    {
      initialData: {
        count: 0,
        timeLeft: 0,
        secondsLeft: 0,
        status: '',
        configs: [],
      },
      queryKey: ['activeInstances'],
      refetchInterval: INSTANCE_REFRESH_INTERVAL,
    }
  );

  const startInstanceMutation = useApiMutation<StartInstance>(
    '/instance-management/start',
    {
      method: 'POST',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const updateInstanceMutation = useApiMutation<{ instanceCount: number }>(
    '/instance-management/update',
    {
      method: 'PATCH',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const shutDownInstanceMutation = useApiMutation<void>(
    '/instance-management/stop',
    {
      method: 'DELETE',
    },
    {
      onError: (error) => {
        toast.error(error.message);
      },
    }
  );

  const isFormDisabled = useMemo(
    () => ['Updating', 'Creating'].includes(data.status),
    [data.status]
  );

  const launchInstance = async (formData) => {
    if (Number(data.count) >= 1) {
      await updateInstanceMutation.mutateAsync({
        instanceCount: Number(formData.instanceCount),
      });
    } else {
      await startInstanceMutation.mutateAsync({
        instanceCount: Number(formData.instanceCount),
        configs: formData.configs,
        duration: formData.duration,
      });
    }

    await refetch();
    toast.success('Instance launched successfully');
  };

  const shutDownInstance = async () => {
    await shutDownInstanceMutation.mutateAsync();
    await refetch();
    toast.success('Instance shut down successfully');
  };

  const configOptions = useMemo(() => {
    const configOptions = Object.values(configs).flat() as unknown as Config[];
    return configOptions.map((config) => ({
      value: config.id,
      id: config.description,
    }));
  }, [configs]);

  const defaultValues = useMemo(() => {
    return {
      configs: data.configs,
      duration: data.timeLeft,
      instanceCount: (data.count || 1).toString(),
    };
  }, [data]);

  if (isFetching) {
    return <Spinner />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="p-6 rounded-2xl shadow-md border flex flex-col gap-4">
        <h3 className="text-2xl font-semibold text-zinc-50">
          Launch Instances
        </h3>

        <InstanceForm
          status={data.status}
          disabled={isFormDisabled}
          onSubmit={launchInstance}
          configOptions={configOptions}
          defaultValues={defaultValues}
          isLoading={startInstanceMutation.isPending}
        />
      </div>

      <div className="p-6 rounded-2xl shadow-md border flex flex-col gap-4">
        <h3 className="text-2xl font-semibold text-zinc-50">Launch Status</h3>

        <p>{data.status}</p>
      </div>

      {data.timeLeft > 0 && (
        <div className="mt-6 flex flex-col gap-2">
          <h3 className="text-2xl font-semibold text-zinc-50">
            Active Batches
          </h3>

          <div className="space-y-4">
            <div className="flex justify-between items-center rounded-xl p-4 border">
              <CountdownCircleTimer
                isPlaying
                size={250}
                duration={data.secondsLeft}
                updateInterval={1}
                colors={['#64B5F6', '#81C784', '#AED581', '#FFF59D']}
                colorsTime={[7, 5, 2, 0]}
              >
                {({ remainingTime }) => {
                  const duration = intervalToDuration({
                    start: 0,
                    end: remainingTime * 1000,
                  });

                  return (
                    <h3 className="text-2xl font-semibold text-zinc-50">
                      {`${duration.hours ? duration.hours + 'h ' : ''} ${duration.minutes ? duration.minutes + 'm ' : ''} ${duration.seconds ? duration.seconds + 's' : ''}`}
                    </h3>
                  );
                }}
              </CountdownCircleTimer>

              <Button
                variant="destructive"
                disabled={isFormDisabled}
                onClick={() => setIsShuttingDown(true)}
              >
                Shut down now
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={isShuttingDown} onOpenChange={setIsShuttingDown}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Shutdown</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to shut down this batch?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction
              onClick={shutDownInstance}
              className="text-destructive border-destructive hover:text-destructive"
              data-testid="shut-down-button"
            >
              Shut down
            </AlertDialogAction>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
